import {
  computed,
  onScopeDispose,
  ref,
  shallowRef,
  toValue,
  type ComputedRef,
  type MaybeRefOrGetter,
  type Ref,
  type ShallowRef,
} from 'vue'

import { reportApi } from '../api/ReportApi'
import { sheetSharingApi } from '../api/SheetSharingApi'
import type { ReportColumn } from '../models/column.types'
import type { ReportExportAction, ReportExportOutcome } from '../models/export.types'
import type { ReportRow, ReportSpec } from '../models/report.types'
import { executeReportExport } from '../service/reportExport'
import { normalizeShareOptions, sheetShareSupported } from '../service/sheetShare'

import { useReportSchedules } from './useReportSchedules'

import { useAccount, useWorkspace } from '@/features/account'
import { exportXlsx, getSafeXlsxFilename } from '@/shared/lib/export'

export interface UseReportExportOptions {
  spec: MaybeRefOrGetter<ReportSpec | null>
  rows: MaybeRefOrGetter<readonly ReportRow[]>
  columns: MaybeRefOrGetter<readonly ReportColumn[]>
  /** False while the rows/columns do not belong to the current request spec. */
  enabled?: MaybeRefOrGetter<boolean>
  totalRowCount?: MaybeRefOrGetter<number | undefined>
  name: MaybeRefOrGetter<string>
  workspaceIds?: MaybeRefOrGetter<readonly (number | string)[]>
  asyncEligible?: MaybeRefOrGetter<boolean>
  productPrecalculated?: MaybeRefOrGetter<boolean>
  currencySuffix?: MaybeRefOrGetter<string | undefined>
  remotePresentationCompatible?: MaybeRefOrGetter<boolean>
  presentFetchedPresentation?: MaybeRefOrGetter<
    ((rows: readonly ReportRow[]) => { rows: ReportRow[]; columns: ReportColumn[] }) | undefined
  >
}

export interface UseReportExport {
  busy: Ref<boolean>
  error: ShallowRef<unknown | null>
  lastResult: ShallowRef<ReportExportOutcome | null>
  canExport: ComputedRef<boolean>
  exportSheet: (
    recipients?: readonly string[],
    shareWith?: readonly string[],
  ) => Promise<ReportExportOutcome | null>
  /** The sharing-list addresses the picker may offer; `[]` where the route ignores `share_with`. */
  loadShareOptions: () => Promise<string[]>
  downloadXlsx: (recipients?: readonly string[]) => Promise<ReportExportOutcome | null>
  cancel: () => void
}

function resolveExportRecipients(
  requested: readonly string[],
  fallback: string | undefined,
): string[] {
  if (requested.length > 0) return [...requested]
  return fallback ? [fallback] : []
}

/** Report-owned export trigger: request shape/threshold here, file bytes in module 16's adapter. */
export function useReportExport(options: UseReportExportOptions): UseReportExport {
  const account = useAccount()
  const workspace = useWorkspace()
  const schedules = useReportSchedules()
  const busy = ref(false)
  const error = shallowRef<unknown | null>(null)
  const lastResult = shallowRef<ReportExportOutcome | null>(null)
  let controller: AbortController | null = null
  let activeRequest = 0

  const canExport = computed(
    () =>
      (options.enabled === undefined || toValue(options.enabled)) &&
      toValue(options.spec) !== null &&
      toValue(options.columns).some((column) => !column.invisible) &&
      account.accountId.value !== undefined &&
      workspace.workspaceId.value !== undefined,
  )

  const cancel = (): void => {
    activeRequest += 1
    controller?.abort()
    controller = null
    busy.value = false
  }

  const productPrecalculatedNow = (): boolean =>
    options.productPrecalculated === undefined
      ? Boolean(workspace.getAttribute('product_precalculated_config')?.value)
      : toValue(options.productPrecalculated)

  const loadShareOptions = async (): Promise<string[]> => {
    const accountId = account.accountId.value
    const workspaceId = workspace.workspaceId.value
    if (accountId === undefined || workspaceId === undefined) return []
    const supported = sheetShareSupported({
      spec: toValue(options.spec),
      workspaceIds: toValue(options.workspaceIds) ?? [],
      productPrecalculated: productPrecalculatedNow(),
    })
    if (!supported) return []
    try {
      return normalizeShareOptions(await sheetSharingApi.allowedEmails(accountId, workspaceId))
    } catch {
      // No sharing list is not an export failure: the export simply goes ahead unshared.
      return []
    }
  }

  const run = async (
    action: ReportExportAction,
    recipients: readonly string[] = [],
    shareWith: readonly string[] = [],
  ): Promise<ReportExportOutcome | null> => {
    const spec = toValue(options.spec)
    const accountId = account.accountId.value
    const workspaceId = workspace.workspaceId.value
    if (!spec || accountId === undefined || workspaceId === undefined || !canExport.value)
      return null

    const workspaceIds = toValue(options.workspaceIds) ?? []
    const totalRowCount = toValue(options.totalRowCount)
    const remotePresentationCompatible = toValue(options.remotePresentationCompatible)
    const presentFetchedPresentation = toValue(options.presentFetchedPresentation)
    const productPrecalculated = productPrecalculatedNow()
    const fallbackRecipient = account.userEmail?.value?.trim()
    const resolvedRecipients = resolveExportRecipients(recipients, fallbackRecipient)
    const schedule =
      spec.report_id === undefined || workspaceIds.length > 0 || spec.config_id !== undefined
        ? null
        : schedules.getScheduleByReportId(spec.report_id)
    const needsSchedule =
      spec.config_id === undefined &&
      workspaceIds.length === 0 &&
      !(productPrecalculated && spec.partition_name === 'metrics_product')
    if (needsSchedule && !schedule) return null

    controller?.abort()
    controller = new AbortController()
    const requestId = ++activeRequest
    busy.value = true
    error.value = null
    try {
      const result = await executeReportExport(
        {
          action,
          name: toValue(options.name),
          spec,
          rows: toValue(options.rows),
          columns: toValue(options.columns),
          ...(totalRowCount === undefined ? {} : { totalRowCount }),
          asyncEligible:
            toValue(options.asyncEligible) ??
            (workspaceIds.length === 0 && action === 'xlsx' && resolvedRecipients.length > 0),
          recipients: resolvedRecipients,
          ...(shareWith.length ? { shareWith } : {}),
          ...(remotePresentationCompatible === undefined ? {} : { remotePresentationCompatible }),
          ...(presentFetchedPresentation === undefined ? {} : { presentFetchedPresentation }),
        },
        {
          accountId,
          workspaceId,
          workspaceIds,
          ...(schedule ? { scheduleId: schedule.id } : {}),
          productPrecalculated,
        },
        reportApi,
        {
          write: async (prepared) => {
            const currencySuffix = toValue(options.currencySuffix)
            await exportXlsx(
              [{ name: prepared.name, data: prepared.rows, columns: prepared.columns }],
              prepared.name,
              {
                telemetry: { fileFormat: 'xlsx', featureName: 'reports' },
                ...(currencySuffix === undefined ? {} : { currencySuffix }),
              },
            )
            return getSafeXlsxFilename(prepared.name)
          },
        },
        controller.signal,
      )
      if (requestId !== activeRequest || controller.signal.aborted) return null
      lastResult.value = result
      return result
    } catch (cause) {
      if (requestId !== activeRequest || controller.signal.aborted) return null
      error.value = cause
      return null
    } finally {
      if (requestId === activeRequest) {
        busy.value = false
        controller = null
      }
    }
  }

  onScopeDispose(cancel)
  return {
    busy,
    error,
    lastResult,
    canExport,
    exportSheet: (recipients, shareWith) => run('sheet', recipients, shareWith),
    loadShareOptions,
    downloadXlsx: (recipients) => run('xlsx', recipients),
    cancel,
  }
}
